import { EMPTY_ARRAY } from '@e-socna/empty';
import type { Id } from '@e-socna/data-layer';

import type { ActorsCmp_ActorableRules } from '../../../components/Actors/fragments/actorable-rules.fragment';
import type { RepresentativeTypeRules } from '../../../components/Actors/utils/missing-representative-types.util';

export type RepresentationRules = Pick<
  ActorsCmp_ActorableRules,
  | 'atLeastOneRepresentativeForQuotationReceiver'
  | 'atLeastOneRepresentativeForEachExternalOrganization'
> &
  RepresentativeTypeRules;

export const NO_REPRESENTATION_RULES: RepresentationRules = {
  atLeastOneRepresentativeForQuotationReceiver: false,
  atLeastOneRepresentativeForEachExternalOrganization: false,
  requiredQuotationEmitterRepresentativeTypeIds: EMPTY_ARRAY,
  requiredQuotationReceiverRepresentativeTypeIds: EMPTY_ARRAY,
};

export interface RepresentationReferences {
  readonly quotationReceiverCategoryId: Id;
  readonly quotationEmitterCategoryId: Id;
}

export interface RepresentedOrganization {
  readonly isInternal: boolean;
  readonly categoryIds: readonly Id[];
}

// The values of the actor form validated, as Yup gives them (untyped).
export const representedOrganizationOf = (
  values: Readonly<Partial<RepresentedOrganization>>,
): RepresentedOrganization => ({
  isInternal: !!values.isInternal,
  categoryIds: values.categoryIds ?? [],
});

const isQuotationReceiver = (
  { isInternal, categoryIds }: RepresentedOrganization,
  references: RepresentationReferences,
) =>
  !isInternal && categoryIds.includes(references.quotationReceiverCategoryId);

// Why an organization actor must have a representative (the back checks the same on the
// effective external actors): the French reason, or null when it may have none.
export const representativeRequirementOf = (
  organization: RepresentedOrganization,
  rules: RepresentationRules,
  references: RepresentationReferences,
): string | null => {
  if (organization.isInternal) {
    return 'Une organisation interne doit avoir au moins un représentant.';
  }

  if (rules.atLeastOneRepresentativeForEachExternalOrganization) {
    return 'Chaque organisation externe a au moins un représentant.';
  }

  if (
    rules.atLeastOneRepresentativeForQuotationReceiver &&
    isQuotationReceiver(organization, references)
  ) {
    return 'Le destinataire du devis (organisation) a au moins un représentant.';
  }

  return null;
};
